import {
  useMutation,
  useQuery,
  useQueryClient,
  useQueries,
} from '@tanstack/react-query'
import { App, Button, Popconfirm } from 'antd'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { evoConsolidation } from '$api'
import { evoClient, putPart, fetchModel3DFile } from '$api/client'
import { fetchAllServices, postApplyTexturing } from '$api/evoAPIs'
import { updateStatusAttributes } from '$utils'
import StatusPage from '../Defaultwizard/StatusPage'
import StepOneSelection from '../Defaultwizard/StepOneSelection'
import StepThreeSummary from '../Defaultwizard/StepThreeSummary'
import {
  StyledStepButtons,
  StyledSteps,
} from '../Defaultwizard/styledComponents'
import StepTwoTexturingConfig from './StepTwoTexturingConfig'
import { useFreshToken } from '$context/user'
import PropTypes from 'prop-types'

const ApplyTexturing = (props) => {
  const { t } = useTranslation()
  const [fcData] = useState({
    not_feasible_parts: [],
    feasible_parts: [],
  })
  const [bookableParts, setBookableParts] = useState()
  const [steps, setSteps] = useState([
    {
      title: t('service.selection'),
      content: '',
    },
    {
      title: t('service.apply_texturing.step_config_title'),
      content: '',
    },
    {
      title: t('service.order'),
      content: '',
    },
  ])
  const [current, setCurrent] = useState(0)
  const { message } = App.useApp()
  const [visible, setVisible] = useState(true)
  const [loading, setLoading] = useState(false)
  const [buttonActive, setButtonActive] = useState(true)
  const [currentFormValues, setCurrentFormValues] = useState()
  const [configState, setConfigState] = useState(true)
  const [accumulatedFormData, setAccumulatedFormData] = useState([])
  const rowDataRef = useRef({})
  const pendingMutations = useRef(0)
  const queryClient = useQueryClient()
  const syncFreshToken = useFreshToken()
  const { data: servicesData } = useQuery(['services'], fetchAllServices)

  const addBookedApplyTexturings = useMutation(
    async (data) => {
      data.service_name = 'Apply Texturing'
      data.service_id = props.bookedServiceStatus[1].id
      data.partlist_name = props.activeGroupName
      data.part.files = []

      const formData = new FormData()
      formData.append('model_3d_file', data.model_3d_file)
      if (data.user_texture_file.isPreset) {
        formData.append('texture_id', data.user_texture_file.textureId)
      } else {
        formData.append(
          'texture_file',
          data.user_texture_file.file,
          data.user_texture_file.file.name,
        )
      }
      delete data.model_3d_file
      delete data.user_texture_file
      formData.append('apply_texturing_data', JSON.stringify(data))
      syncFreshToken()
      try {
        return await evoConsolidation.post(postApplyTexturing, formData)
      } catch (error) {
        if (error.response?.status === 401) {
          syncFreshToken()
          return await evoConsolidation.post(postApplyTexturing, formData)
        }
        throw error
      }
    },
    {
      onSuccess: (res) => {
        updateClientPartStatus.mutate(res.data)
      },
    },
    {
      onError: (err) => {
        setLoading(false)
        message.error(`${err.response.status} Could not book Service`)
      },
    },
  )

  const updateClientPartStatus = useMutation(
    (partsToUpdate) => {
      //check if "partsToUpdate" is from feasibility check or booked service
      if (partsToUpdate.part) {
        const editedPart = updateStatusAttributes(
          partsToUpdate.part,
          'status_attributes',
          'Apply Texturing',
          '1',
        )
        editedPart.png = 'nopng'
        return evoClient.put(
          `${putPart}${editedPart.db_id_client}`,
          editedPart,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
      }
    },
    {
      onSuccess: () => {
        pendingMutations.current -= 1
        if (pendingMutations.current === 0) {
          setLoading(false)
          queryClient.invalidateQueries(['allgroups'])
          queryClient.invalidateQueries(['notifications'])
        }
      },
      onError: (err) => {
        setLoading(false)
        message.error(`${err.response.status} Could not update status`)
      },
    },
  )

  const updateAccumulatedFormData = useCallback(() => {
    if (currentFormValues) {
      setAccumulatedFormData((prevData) => {
        if (
          prevData.some(
            (item) =>
              item.part.db_id_client === currentFormValues.part.db_id_client,
          )
        ) {
          return prevData.map((item) =>
            item.part.db_id_client === currentFormValues.part.db_id_client
              ? currentFormValues
              : item,
          )
        } else {
          return [...prevData, currentFormValues]
        }
      })
    }
  }, [currentFormValues])

  const updateSteps = useCallback(() => {
    if (bookableParts || servicesData) {
      const newSteps = [
        {
          title: t('service.selection'),
          content: (
            <StepOneSelection
              fcData={fcData}
              selectedParts={props.selectedParts}
              setBookableParts={setBookableParts}
              bookableParts={bookableParts}
              servicesData={servicesData}
              bookedServiceStatus={props.bookedServiceStatus}
              setConfigState={setConfigState}
            />
          ),
        },
        {
          title: t('service.apply_texturing.step_config_title'),
          content: (
            <StepTwoTexturingConfig
              fcData={fcData}
              selectedParts={props.selectedParts}
              bookableParts={bookableParts}
              setCurrentFormValues={setCurrentFormValues}
              setConfigState={setConfigState}
              rowDataRef={rowDataRef}
            />
          ),
        },
        {
          title: t('service.order'),
          content: (
            <StepThreeSummary
              fcData={fcData}
              selectedParts={props.selectedParts}
              bookableParts={bookableParts}
              setButtonActive={setButtonActive}
            />
          ),
        },
      ]
      setSteps(newSteps)
    }
  }, [
    bookableParts,
    servicesData,
    fcData,
    props.selectedParts,
    setBookableParts,
    setCurrentFormValues,
    setConfigState,
    setButtonActive,
    props.bookedServiceStatus,
    t,
  ])

  const handleOk = () => {
    setCurrent(0)
    props.setSelectedParts([])
    setButtonActive(true)
    setLoading(true)
    setVisible(false)

    pendingMutations.current = accumulatedFormData.length

    //post to endpoint
    for (let formData of accumulatedFormData) {
      addBookedApplyTexturings.mutate({
        ...formData,
        originalPart: bookableParts,
      })
    }
  }

  const next = () => {
    if (current === 0) {
      setConfigState(false)
    }
    setCurrent(current + 1)
  }
  const prev = () => {
    if (current === 1) {
      setConfigState(true)
    }
    setCurrent(current - 1)
  }
  const items = steps
    .filter(function (x) {
      return x !== null
    })
    .map((item) => ({
      key: item.title,
      title: item.title,
    }))

  useEffect(() => {
    updateSteps()
  }, [updateSteps])

  useEffect(() => {
    updateAccumulatedFormData()
  }, [updateAccumulatedFormData])

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ['model3DFile'] })
    }
  }, [])

  useQueries({
    queries: (bookableParts?.bookableParts ?? []).map((part) => ({
      queryKey: ['model3DFile', part.id],
      queryFn: ({ signal }) => fetchModel3DFile(part.id, signal),
      staleTime: Infinity,
    })),
  })

  return (
    <>
      {visible && (
        <>
          <h1 style={{ margin: '50px 0' }}>
            {t(props.bookedServiceStatus[1].servicename)}
          </h1>
          <div className="contentcard">
            <StyledSteps current={current} items={items} type="navigation" />
            <div style={{ margin: '50px 0px' }}>{steps[current].content}</div>
            <StyledStepButtons>
              {current < steps.length - 1 && (
                <Popconfirm
                  disabled={configState}
                  placement="topRight"
                  description={
                    <>
                      Some parts need configuration before proceeding.
                      <br />
                      Check the &quot;status&quot; of your part.
                    </>
                  }
                  title={<h4>Some changes have to be made</h4>}
                >
                  <Button
                    type="primary"
                    onClick={() => next()}
                    disabled={!configState}
                  >
                    {t('button.next')}
                  </Button>
                </Popconfirm>
              )}
              {current === steps.length - 1 && (
                <Button
                  type="primary"
                  onClick={() => handleOk()}
                  disabled={!buttonActive}
                >
                  {t('button.order')}
                </Button>
              )}
              {current > 0 && (
                <Button
                  style={{
                    margin: '0 8px',
                  }}
                  onClick={() => prev()}
                >
                  {t('button.back')}
                </Button>
              )}
            </StyledStepButtons>
          </div>
        </>
      )}
      {!visible && (
        <>
          <StatusPage
            setIsBookModalOpen={props.setIsBookModalOpen}
            loading={loading}
            setCurrent={props.setCurrent}
          />
        </>
      )}
    </>
  )
}

ApplyTexturing.propTypes = {
  bookedServiceStatus: PropTypes.array.isRequired,
  activeGroupName: PropTypes.string.isRequired,
}

export default ApplyTexturing
